import './Nav.css';
import typeLogo from '../../images/find-a-film-logo-on-dark.png';
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { AppNotification, useNotifications } from '../../notifications/NotificationContext';
import { useProfile } from '../../profile/ProfileContext';

// The same five destinations as the mobile app's tab bar.
const tabs = [
  { to: '/', label: 'Home', icon: '⌂', end: true },
  { to: '/discover', label: 'Discover', icon: '✦' },
  { to: '/history', label: 'My films', icon: '▤', auth: true },
  { to: '/together', label: 'Rooms', icon: '◎', auth: true },
  { to: '/account', label: 'Account', icon: '○' },
];

const Bell = () => {
  const { items, unreadCount, markRead, respond, alertsEnabled, enableAlerts } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => { if (!ref.current?.contains(event.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const openRoom = (item: AppNotification) => { markRead(item.id); setOpen(false); navigate(`/together?room=${item.data.sessionId}`); };
  return <div className="nav-bell" ref={ref}>
    <button className="nav-bell__button" onClick={() => setOpen(value => !value)} aria-label={`Notifications${unreadCount ? `, ${unreadCount} new` : ''}`} aria-expanded={open}>
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9a6 6 0 1 1 12 0c0 4.5 1.5 6.5 2 7H4c.5-.5 2-2.5 2-7Zm4 10a2 2 0 0 0 4 0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
      {unreadCount > 0 && <span className="nav-bell__badge">{unreadCount > 9 ? '9+' : unreadCount}</span>}
    </button>
    {open && <div className="nav-bell__panel" role="dialog" aria-label="Notifications">
      <p className="nav-bell__eyebrow">WHAT'S NEW</p>
      {!alertsEnabled && 'Notification' in window && <button className="nav-bell__alerts" onClick={enableAlerts}>🔔 Alert me when a film night starts</button>}
      {!items.length && <p className="nav-bell__empty">You’re all caught up. Friend requests and room invitations will appear here.</p>}
      {items.map(item => <div key={item.id} className={`nav-bell__item${item.urgent ? ' is-urgent' : ''}`}>
        <span className="nav-bell__icon" aria-hidden="true">{item.type === 'friend-request' ? '♧' : item.type === 'room-action' ? '◉' : '✦'}</span>
        <div><strong>{item.title}</strong><small>{item.body}</small>
          {item.type === 'friend-request'
            ? <div className="nav-bell__actions"><button onClick={() => respond(item, false)}>Decline</button><button className="primary" onClick={() => respond(item, true)}>Accept</button></div>
            : <button className="nav-bell__open" onClick={() => openRoom(item)}>{item.cta || 'Open room'} →</button>}
        </div>
      </div>)}
    </div>}
  </div>;
};

const Nav = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const { liveRooms } = useNotifications();
  const { myAvatarUrl } = useProfile();
  const visibleTabs = tabs.filter(tab => !tab.auth || user);
  return (
    <>
      <nav className='nav' aria-label='Main navigation'>
        <div className='nav__inner'>
          <Link to='/' className='nav__brand' aria-label='Find A Film home'>
            <img className='nav__logo' src={typeLogo} alt='Find A Film' />
          </Link>
          <div className='nav__actions'>
            <NavLink className='nav__link' to='/discover'>Discover</NavLink>
            {user && <NavLink className='nav__link' to='/swipe'>Swipe</NavLink>}
            {user && <NavLink className='nav__link' to='/history'>My films</NavLink>}
            {user && <NavLink className='nav__link' to='/together'>Rooms</NavLink>}
            {user && <Bell />}
            <Link className={`nav__account${myAvatarUrl ? ' nav__account--photo' : ''}`} to='/account'>{myAvatarUrl && <img src={myAvatarUrl} alt='' />}{user ? 'My account' : 'Sign in'}</Link>
          </div>
        </div>
      </nav>
      <nav className='tabbar' aria-label='Sections'>
        {visibleTabs.map(tab => <NavLink key={tab.to} to={tab.to} end={tab.end} className={({ isActive }) => `tabbar__item${isActive || (tab.to === '/account' && pathname.startsWith('/friends')) ? ' is-active' : ''}`}>
          {tab.to === '/account' && myAvatarUrl ? <img className="tabbar__photo" src={myAvatarUrl} alt="" /> : <span aria-hidden="true">{tab.icon}</span>}{tab.label}{tab.to === '/together' && liveRooms.length > 0 && <b className="tabbar__badge" aria-label={`${liveRooms.length} rooms need you`}>{liveRooms.length}</b>}
        </NavLink>)}
      </nav>
    </>
  );
};

export default Nav;
